import React from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import { Card, CardContent, CardHeader, Divider, Button } from '@material-ui/core';
import { Progress, Table } from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';
import { Link } from 'react-router-dom';

type ApiEntity = Entity & { kind: 'API' };

const typeLabel = (raw?: string) => {
    const t = (raw ?? '').toLowerCase();
    if (t === 'openapi') return 'OpenAPI';
    if (t === 'asyncapi') return 'AsyncAPI';
    if (t === 'graphql') return 'GraphQL';
    return raw ?? 'Unknown';
};

const getApiType = (e: Entity): string => ((e as any)?.spec?.type ?? '').toString().toLowerCase();

const denseCellStyleName = { 
  fontWeight:700,
  paddingTop: 12,
  paddingBottom: 12,
};

const denseCellStyle = { 
  paddingTop: 12,
  paddingBottom: 12,
};

const apiColumns = [
    {
        title: 'Name',
        field: 'metadata.name',
        render: (e: Entity) => <EntityRefLink entityRef={e} />,
        width: '40%',
        cellStyle: denseCellStyleName
    },
    {
        title: 'Type',
        field: 'spec.type',
        render: (e: Entity) => typeLabel((e as any)?.spec?.type),
        width: '15%',
        cellStyle: {
            ...denseCellStyle,
            whiteSpace: 'nowrap'
        },
    },
    {
        title: 'Description',
        field: 'metadata.description',
        render: (e: Entity) => e.metadata?.description ?? '-',
        width: '45%',
        cellStyle: denseCellStyle,
    },
];

const graphqlApiColumns = [
    ...apiColumns,
    {
        title: '',
        field: 'metadata.name',
        width: '10%',
        sorting: false,
        render: (e: Entity) => (
            <Button
                component={Link}
                to={`/catalog/${e.metadata.namespace ?? 'default'}/api/${e.metadata.name}/graphiql`}
                size="small"
                variant="outlined"
                color="primary"
            >
                Explore
            </Button>
        ),
        cellStyle: denseCellStyle,
    },
];

function sortByName(a: Entity, b: Entity) {
    const an = (a.metadata?.title ?? a.metadata?.name ?? '').toString().toLowerCase();
    const bn = (b.metadata?.title ?? b.metadata?.name ?? '').toString().toLowerCase();
    return an.localeCompare(bn);
}

export const SystemApisCard = () => {
    const { entity: system } = useEntity();
    const catalogApi = useApi(catalogApiRef);

    const [loading, setLoading] = React.useState(true);
    const [apis, setApis] = React.useState<ApiEntity[]>([]);
    const [error, setError] = React.useState<string | undefined>();

    React.useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                setLoading(true);
                setError(undefined);

                const systemName = system.metadata.name;
                const systemNamespace = system.metadata.namespace ?? 'default';

                // Many orgs model this as spec.system on the API entity
                const res = await catalogApi.getEntities({
                    filter: [
                        { kind: 'API', 'spec.system': systemName, 'metadata.namespace': systemNamespace },
                        // fallback if APIs omit namespace alignment
                        { kind: 'API', 'spec.system': systemName },
                    ],
                });

                const items = (res.items ?? []) as ApiEntity[];

                if (mounted) {
                    setApis(items);
                    setLoading(false);
                }
            } catch (e: any) {
                if (mounted) {
                    setError(e?.message ?? String(e));
                    setLoading(false);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [catalogApi, system]);

    const openApis = apis.filter(a => getApiType(a) === 'openapi').sort(sortByName);
    const asyncApis = apis.filter(a => getApiType(a) === 'asyncapi').sort(sortByName);
    const graphApis = apis.filter(a => getApiType(a) === 'graphql').sort(sortByName);


    return (
        <Card>
            <CardHeader title="APIs" />
            <CardContent>
                {loading && <Progress />}

                {!loading && error && (
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                        Failed to load APIs: {error}
                    </div>
                )}

                {!loading && !error && (
                    <>
                        {openApis.length > 0 && (
                            <>
                                <h4 style={{ marginTop: 0 }}>OpenAPI</h4>
                                <Table
                                    options={{ paging: false, search: true , sorting:false}}
                                    data={openApis}
                                    columns={apiColumns as any}
                                />
                                <Divider style={{ margin: '16px 0' }} />
                            </>
                        )}

                        {asyncApis.length > 0 && (
                            <>
                                <h4 style={{ marginTop: 0 }}>AsyncAPI</h4>
                                <Table
                                    options={{ paging: false, search: false , sorting:false}}
                                    data={asyncApis}
                                    columns={apiColumns as any}
                                />
                                <Divider style={{ margin: '16px 0' }} />
                            </>
                        )}

                        {graphApis.length > 0 && (
                            <>
                                <h4 style={{ marginTop: 0 }}>GraphQL</h4>
                                <Table
                                    options={{ paging: false, search: false, sorting: false }}
                                    data={graphApis}
                                    columns={graphqlApiColumns as any}
                                />
                                <Divider style={{ margin: '16px 0' }} />
                            </>
                        )}

                        {apis.length === 0 && <div>No APIs</div>}
                    </>
                )}
            </CardContent>
        </Card>
    );
};
